<template>
  <div class="picker-page" v-if="value">
    <div class="picker-head">
      <div class="picker-nav">
        <button class="picker-back" type="button" @click="close"></button>
        <div class="dialog-title">{{ meta.title }}</div>
      </div>
      <div class="search-bar">
        <input
          v-model="keyword"
          type="search"
          enterkeyhint="search"
          :placeholder="meta.placeholder"
          @keyup.enter="search"
          @search="search"
        >
        <button class="search-btn" type="button" @click="search">
          <i class="search-glass"></i>
          <span>搜索</span>
        </button>
      </div>
    </div>
    <div class="picker-list" :class="meta.listClass">
      <van-list
        v-if="source !== 'template'"
        v-model="loading"
        :finished="finished"
        :immediate-check="false"
        finished-text="没有更多了"
        @load="loadMore"
      >
        <template v-if="source === 'course'">
          <div class="student-row" v-for="item in list" :key="item.id" @click="onSelect(item)">
            <img class="check-icon" :src="selectedId === item.id ? checkedIcon : emptyIcon" alt="">
            <img class="course-thumb" :src="cover" :title="item.title" alt="">
            <div class="student-main">
              <div class="student-name">{{ item.title }}</div>
              <div class="student-hospital" v-if="item.hospital">医院：{{ item.hospital }}</div>
              <div class="student-hospital" v-if="item.lecturer">讲师：{{ item.lecturer }}</div>
            </div>
          </div>
        </template>
        <template v-if="source === 'question'">
          <div class="question-card" v-for="item in list" :key="item.id" @click="onSelect(item)">
            <img class="check-icon" :src="isQuestionOn(item.id) ? checkedIcon : emptyIcon" alt="">
            <div class="question-main">
              <div class="question-title">{{ item.title }}</div>
              <div class="question-meta">
                <span class="q-tag">{{ item.tag }}</span>
                <span class="q-code">{{ item.code }}</span>
              </div>
            </div>
          </div>
        </template>
      </van-list>
      <template v-else>
        <div class="picker-state" v-if="!loading && !loadFailed && !list.length">暂无可用模板</div>
        <div
          class="template-item"
          v-for="item in list"
          :key="item.id"
          @click="onSelect(item)"
        >
          <div class="template-name">{{ item.name }}</div>
          <div class="template-content">{{ item.content }}</div>
        </div>
      </template>
    </div>
    <div class="picker-actions">
      <button type="button" class="btn-cancel" @click="close">取消</button>
      <button v-if="meta.showConfirm" type="button" class="btn-ok" @click="onConfirm">确定</button>
    </div>
  </div>
</template>

<script>
import {
  getResearchCampDispatchCandidates,
  getResearchCampMessageTemplates
} from '@/api/researchCamp'

export default {
  name: 'CampPickerPage',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    // course 课程，question 考试题目，template 消息模版
    source: {
      type: String,
      default: 'course'
    },
    campConfigId: {
      type: [Number, String],
      default: ''
    },
    selectedId: {
      type: [Number, String],
      default: ''
    },
    selectedIds: {
      type: Array,
      default () {
        return []
      }
    }
  },
  data () {
    return {
      cover: require('@/assets/images/researchCamp/banner.png'),
      checkedIcon: require('@/assets/images/researchCamp/ic_x_x.svg'),
      emptyIcon: require('@/assets/images/researchCamp/ic_x_w.svg'),
      keyword: '',
      applied: '',
      list: [],
      loading: false,
      finished: false,
      loadFailed: false,
      pageNum: 1,
      requestSeq: 0
    }
  },
  computed: {
    // 标题、搜索提示和列表样式按来源区分
    meta () {
      const pages = {
        course: {
          title: '选择课程',
          placeholder: '搜索课程/讲师',
          listClass: '',
          showConfirm: true
        },
        question: {
          title: '选择考试题目',
          placeholder: '搜索分类/编号/题目',
          listClass: 'is-card',
          showConfirm: true
        },
        template: {
          title: '选择模版',
          placeholder: '搜索模版名称',
          listClass: 'is-plain',
          showConfirm: false
        }
      }
      return pages[this.source] || pages.course
    }
  },
  watch: {
    // 打开时重新拉列表，并多压一层历史
    value (visible) {
      if (!visible) return
      this.keyword = ''
      this.applied = ''
      this.load(false)
      this.pushHistory()
    }
  },
  created () {
    this.historyOn = false
    this.historyClosing = false
    this.skipRestore = false
  },
  beforeDestroy () {
    window.removeEventListener('popstate', this.onPopstate)
  },
  methods: {
    // 点搜索、回车或键盘右下角搜索才按关键字请求
    search () {
      this.applied = this.keyword.trim()
      this.load(false)
    },
    isQuestionOn (id) {
      return this.selectedIds.indexOf(id) > -1
    },
    // 课程和题目交给外层记勾选，模版点中后直接套用并关闭
    onSelect (item) {
      this.$emit('select', item)
      if (this.source === 'template') this.commit()
    },
    loadMore () {
      if (!this.finished) this.load(true)
    },
    async load (append) {
      if (!this.campConfigId) {
        this.$toast('未获取到研修营信息')
        this.finished = true
        return
      }
      if (this.source === 'template') {
        await this.loadTemplates()
        return
      }
      await this.loadCandidates(append)
    },
    async loadCandidates (append) {
      if (!append) {
        this.requestSeq += 1
        this.pageNum = 1
        this.finished = false
        this.list = []
      }
      const requestSeq = this.requestSeq
      const pageNum = append ? this.pageNum : 1
      const type = this.source === 'course' ? 'COURSE' : 'QUESTION'
      this.loading = true
      try {
        const response = await getResearchCampDispatchCandidates(this.campConfigId, {
          type,
          keyword: this.applied,
          pageNum,
          pageSize: 10
        })
        const page = response && response.data ? response.data : {}
        const rows = (page.list || []).map(item => {
          if (type === 'COURSE') {
            return {
              id: String(item.resourceId),
              title: item.name || '',
              hospital: item.hospitalName || '',
              lecturer: item.courseLecturerNames || ''
            }
          }
          return {
            id: String(item.questionId),
            title: item.title || '',
            tag: item.category || '',
            code: item.code || '',
            score: 0
          }
        })
        if (requestSeq !== this.requestSeq) return
        const nextRows = append
          ? this.list.concat(rows.filter(row => !this.list.some(current => current.id === row.id)))
          : rows
        this.list = nextRows
        this.pageNum = (Number(page.pageNum) || pageNum) + 1
        this.finished = Number(page.pageNum) >= Number(page.pages) || rows.length < 10
      } catch (error) {
        if (requestSeq === this.requestSeq) {
          this.finished = true
          if (!append) this.list = []
        }
        this.$toast((error && error.msg) || '候选数据加载失败')
      } finally {
        if (requestSeq === this.requestSeq) this.loading = false
      }
    },
    async loadTemplates () {
      const requestSeq = ++this.requestSeq
      const title = this.applied
      this.loading = true
      this.loadFailed = false
      this.list = []
      try {
        const firstResponse = await getResearchCampMessageTemplates({ title, pageNum: 1, pageSize: 500 })
        const firstPage = firstResponse && firstResponse.data ? firstResponse.data : {}
        const allTemplates = Array.isArray(firstPage.list) ? firstPage.list.slice() : []
        const totalPages = Number(firstPage.pages) || 1
        for (let pageNum = 2; pageNum <= totalPages; pageNum += 1) {
          if (requestSeq !== this.requestSeq) return
          const response = await getResearchCampMessageTemplates({ title, pageNum, pageSize: 500 })
          if (requestSeq !== this.requestSeq) return
          const page = response && response.data ? response.data : {}
          if (Array.isArray(page.list)) allTemplates.push(...page.list)
        }
        if (requestSeq !== this.requestSeq) return
        this.list = allTemplates
          .filter(item => Number(item.status) === 1)
          .map(item => ({
            id: String(item.id),
            name: item.title || '',
            content: item.content || ''
          }))
      } catch (error) {
        if (requestSeq !== this.requestSeq) return
        this.list = []
        this.loadFailed = true
        this.$toast((error && error.msg) || '模板加载失败')
      } finally {
        if (requestSeq === this.requestSeq) this.loading = false
      }
    },
    pushHistory () {
      this.skipRestore = false
      if (this.historyOn) return
      try {
        window.history.pushState(Object.assign({}, window.history.state, { pickerLayer: true }), '')
      } catch (error) {
        return
      }
      this.historyOn = true
      this.historyClosing = false
      window.addEventListener('popstate', this.onPopstate)
    },
    onPopstate () {
      const skipRestore = this.skipRestore
      this.historyOn = false
      this.historyClosing = false
      this.skipRestore = false
      window.removeEventListener('popstate', this.onPopstate)
      if (!skipRestore) this.$emit('cancel')
      this.$emit('input', false)
    },
    // 返回和取消丢掉本次修改
    close () {
      if (this.historyClosing) return
      if (this.historyOn) {
        this.historyClosing = true
        window.history.back()
        return
      }
      this.$emit('cancel')
      this.$emit('input', false)
    },
    onConfirm () {
      this.$emit('confirm')
      this.commit()
    },
    // 确定，或列表里点选后直接完成
    commit () {
      this.skipRestore = true
      this.$emit('input', false)
      if (this.historyOn && !this.historyClosing) {
        this.historyClosing = true
        window.history.back()
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '../camp.less';

.picker-page {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9f8f7;
  box-sizing: border-box;
}

.picker-head {
  flex-shrink: 0;
  background: #f9f8f7;
}

.picker-nav {
  position: relative;
}

.dialog-title {
  padding: 6px 44px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 32px;
  color: rgba(0, 0, 0, 0.8);
}

.picker-back {
  position: absolute;
  left: 4px;
  top: 6px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;

  &::before {
    content: '';
    position: absolute;
    left: 12px;
    top: 11px;
    width: 8px;
    height: 8px;
    border-left: 1.5px solid #000;
    border-bottom: 1.5px solid #000;
    transform: rotate(45deg);
  }
}

.search-bar {
  display: flex;
  align-items: center;
  height: 32px;
  margin: 8px 13px 12px;
  padding-left: 14px;
  padding-right: 3px;
  border-radius: 25px;
  background: #fff;

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    font-size: 13px;
    color: #000;
    -webkit-appearance: none;
    appearance: none;

    &::placeholder {
      color: #c1bbb3;
    }

    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration {
      -webkit-appearance: none;
      appearance: none;
    }
  }
}

.search-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 26px;
  border: 0;
  border-radius: 25px;
  background: rgba(213, 207, 201, 0.5);
  font-size: 13px;
  color: #000;
  flex-shrink: 0;
}

.search-glass {
  position: relative;
  width: 9px;
  height: 9px;
  margin-right: 2px;
  border: 1px solid #000;
  border-radius: 50%;
  box-sizing: border-box;

  &::after {
    content: '';
    position: absolute;
    right: -3px;
    bottom: -1px;
    width: 4px;
    height: 1px;
    background: #000;
    transform: rotate(45deg);
  }
}

.picker-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 16px 90px 22px;
  background: #fff;

  &.is-card {
    padding-right: 0;
    padding-left: 0;
    background: transparent;
  }

  &.is-plain {
    padding-right: 0;
    padding-left: 0;
  }
}

.picker-actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  padding: 14px 13px 24px;
  background: #fff;
  box-sizing: border-box;

  button {
    flex: 1;
    height: 40px;
    border: 0;
    border-radius: 30px;
    font-size: 14px;
    font-weight: 400;
  }

  .btn-cancel {
    margin-right: 12px;
    color: #81766f;
    background: #f3f1f0;

    &:only-child {
      margin-right: 0;
    }
  }

  .btn-ok {
    color: #fff;
    background: @camp-red;
  }
}

.course-thumb {
  width: 74px;
  height: 42px;
  margin-right: 8px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.student-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

.check-icon {
  width: 15px;
  height: 15px;
  margin-right: 9px;
  flex-shrink: 0;
}

.student-main,
.question-main {
  flex: 1;
  min-width: 0;
}

.student-name,
.question-title {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000;
}

.student-hospital {
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #81766f;
}

.question-card {
  display: flex;
  align-items: center;
  margin: 0 13px 13px;
  padding: 12px;
  border-radius: 10px;
  background: #fff;

  .check-icon {
    width: 14px;
    height: 14px;
    margin-right: 12px;
  }
}

.question-meta {
  display: flex;
  align-items: center;
  margin-top: 6px;
}

.q-tag {
  height: 18px;
  padding: 0 8px;
  border-radius: 10px;
  background: rgba(235, 23, 0, 0.08);
  font-size: 11px;
  font-weight: 400;
  line-height: 18px;
  color: @camp-red;
}

.q-code {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #81766f;
}

.picker-state {
  padding: 20px 16px;
  color: #a39992;
  text-align: center;
  font-size: 13px;
  line-height: 20px;
}

.template-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f3f3;

  &:last-child {
    border-bottom: 0;
  }
}

.template-name {
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  color: @camp-red;
}

.template-content {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 400;
  line-height: 18px;
  word-break: break-word;
  color: #564c47;
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/components/CampPickerPage.vue